import { useState } from 'react';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import emailjs from '@emailjs/browser';
import { useTranslation } from '../hooks';
import { contactInfo, emailConfig } from '../data';

const Contact = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    projectType: '',
    budget: '',
    description: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const templateParams = {
        to_email: emailConfig.toEmail,
        from_name: formData.name,
        from_email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType,
        budget: formData.budget,
        message: formData.description,
        reply_to: formData.email
      };

      await emailjs.send(
        emailConfig.serviceId,
        emailConfig.templateId,
        templateParams,
        emailConfig.publicKey
      );

      setSubmitMessage(t.contact.form.success);
      setFormData({
        name: '',
        email: '',
        phone: '',
        projectType: '',
        budget: '',
        description: ''
      });
    } catch (error) {
      console.error('Error sending:', error);
      setSubmitMessage(t.contact.form.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const projectTypes = [
    { value: '', label: t.contact.form.projectTypePlaceholder },
    { value: 'villa', label: t.contact.form.projectTypes.villa },
    { value: 'apartment', label: t.contact.form.projectTypes.apartment },
    { value: 'commercial', label: t.contact.form.projectTypes.commercial },
    { value: 'industrial', label: t.contact.form.projectTypes.industrial },
    { value: 'mixed', label: t.contact.form.projectTypes.mixed },
    { value: 'other', label: t.contact.form.projectTypes.other }
  ];

  const budgetRanges = [
    { value: '', label: t.contact.form.budgetPlaceholder },
    { value: 'under500k', label: t.contact.form.budgetRanges.under500k },
    { value: '500k-1m', label: t.contact.form.budgetRanges['500k-1m'] },
    { value: '1m-2m', label: t.contact.form.budgetRanges['1m-2m'] },
    { value: '2m-5m', label: t.contact.form.budgetRanges['2m-5m'] },
    { value: 'over5m', label: t.contact.form.budgetRanges.over5m }
  ];

  const businessHours = [
    { day: t.contact.hours.weekdays, hours: '8:30 - 17:00' },
    { day: t.contact.hours.saturday, hours: '8:30 - 13:00' },
    { day: t.contact.hours.sunday, hours: t.contact.hours.closed }
  ];

  return (
    <section id="contact" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          <div>
            <h2 className="text-4xl lg:text-5xl font-light mb-8 tracking-wide">{t.contact.title}</h2>
            <p className="text-xl text-gray-600 mb-12 leading-relaxed">
              {t.contact.subtitle}
            </p>

            <div className="space-y-8">
              <div className="flex items-start space-x-4">
                <div className="bg-black p-3">
                  <Phone size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2 tracking-wide">{t.contact.phone}</h3>
                  {contactInfo.phones.map((phone, index) => (
                    <p key={index} className="text-gray-600">{phone}</p>
                  ))}
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-black p-3">
                  <Mail size={24} className="text-white" />
                </div>
                <div>
                  <h3 className="font-semibold mb-2 tracking-wide">{t.contact.email}</h3>
                  <p className="text-gray-600">{contactInfo.email}</p>
                </div>
              </div>
            </div>

            <div className="mt-12 p-8 bg-black text-white">
              <h3 className="text-xl font-light mb-4 tracking-wide">{t.contact.hours.title}</h3>
              <div className="space-y-2 text-gray-300">
                {businessHours.map((item, index) => (
                  <div key={index} className="flex justify-between">
                    <span>{item.day}</span>
                    <span>{item.hours}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="bg-gray-50 p-8">
            <h3 className="text-2xl font-light mb-8 tracking-wide">{t.contact.form.title}</h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.form.name} *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors"
                    placeholder={t.contact.form.namePlaceholder}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.form.emailLabel} *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors"
                    placeholder={t.contact.form.emailPlaceholder}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.phone}</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors"
                  placeholder={t.contact.form.phonePlaceholder}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.form.projectType} *</label>
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors"
                >
                  {projectTypes.map((type, index) => (
                    <option key={index} value={type.value}>{type.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.form.budget}</label>
                <select
                  name="budget"
                  value={formData.budget}
                  onChange={handleInputChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors"
                >
                  {budgetRanges.map((range, index) => (
                    <option key={index} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2 tracking-wide">{t.contact.form.description} *</label>
                <textarea
                  rows={6}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-200 focus:border-black focus:outline-none transition-colors resize-none"
                  placeholder={t.contact.form.descriptionPlaceholder}
                />
              </div>

              {submitMessage && (
                <div className={`p-4 rounded ${
                  submitMessage === t.contact.form.success
                    ? 'bg-green-100 text-green-800 border border-green-200'
                    : 'bg-red-100 text-red-800 border border-red-200'
                }`}>
                  {submitMessage}
                </div>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black hover:bg-gray-800 disabled:bg-gray-400 text-white py-4 font-medium transition-colors tracking-wide flex items-center justify-center space-x-2"
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>{t.contact.form.submitting}</span>
                  </>
                ) : (
                  <>
                    <span>{t.contact.form.submit}</span>
                    <ArrowRight size={20} />
                  </>
                )}
              </button>

              <p className="text-sm text-gray-500 text-center leading-relaxed">
                {t.contact.form.consent}
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
